import { captureException } from '@sentry/react';
import { forwardRef, useMemo } from 'react';
import { useToggle } from 'react-use';

import { AccountCard } from '@frontend-libs/portal';

import { AccountModel } from '@portal-clients/account';
import { preferenceClient } from '@portal-clients/preference';
import { HiddenPreferencesModel } from '@portal-clients/user';

import { useLocale } from '@frontend/hooks';
import { useLicenses } from '@frontend/licenses';
import { usePortalConfiguration, useUser } from '@frontend/utils';

export interface AccountCardProps {
  account: AccountModel;
  toggleAccountDetails?: () => void;
}

export const PortalAccountCard = forwardRef<HTMLDivElement, AccountCardProps>(
  (props, ref) => {
    const [showBalance, toggleShowBalance] = useToggle(true);
    const {
      config: {
        features: {
          hidePrice: hidePriceFeature,
          qrCodeIdentification: qrCodeIdentificationFeature,
        },
      },
    } = usePortalConfiguration();
    const { hidePrice } = useLicenses();
    const { key, formatters } = useLocale();
    const { user, updatePreferences } = useUser();

    const hiddenBalances = useMemo(() => {
      if (user?.preferences === undefined) {
        return [];
      }

      if (
        user.preferences?.hidden?.balances.includes(props.account.id) &&
        showBalance
      ) {
        toggleShowBalance(false);
      }

      return user.preferences?.hidden?.balances ?? [];
    }, [user?.preferences?.hidden?.balances]);

    const handleShowBalanceClick = () => {
      if (showBalance) {
        addToHiddenIds(props.account.id).catch((error) => {
          console.error(error);
          captureException(error);
        });
      } else {
        removeFromHiddenIds(props.account.id).catch((error) => {
          console.error(error);
          captureException(error);
        });
      }
      toggleShowBalance();
    };

    const removeFromHiddenIds = async (id: string) => {
      if (!hiddenBalances.includes(id)) {
        return;
      }
      const remainingIds = hiddenBalances.filter(
        (accountId) => accountId !== id,
      );

      const hiddenPreferences: HiddenPreferencesModel = {
        balances: remainingIds,
        infoPanels: user.preferences?.hidden?.infoPanels ?? [],
      };

      updatePreferences({
        hidden: hiddenPreferences,
      });

      await preferenceClient
        .set('hidden', {
          preference: {
            balances: remainingIds,
          },
        })
        .catch((e) => {
          captureException(e, {
            level: 'warning',
            extra: {
              message:
                'Failed to update preferences to display account balance',
            },
          });
        });
    };

    const addToHiddenIds = async (id: string) => {
      if (hiddenBalances.includes(id)) {
        return;
      }
      const ids = [...hiddenBalances, id];

      const hiddenPreferences: HiddenPreferencesModel = {
        balances: ids,
        infoPanels: user.preferences?.hidden?.infoPanels ?? [],
      };

      updatePreferences({
        hidden: hiddenPreferences,
      });

      await preferenceClient
        .set('hidden', {
          preference: {
            balances: ids,
          },
        })
        .catch((e) => {
          captureException(e, {
            level: 'warning',
            extra: {
              message: 'Failed to update preferences to hide account balance',
            },
          });
        });
    };

    return (
      <AccountCard
        ref={ref}
        id={props.account.id}
        type={props.account.type}
        balance={
          props.account.type !== 'DIRECT' ? props.account.balance : undefined
        }
        maxCreditLimit={
          props.account.type === 'CREDIT'
            ? props.account.maxCreditLimit
            : undefined
        }
        displayName={props.account.displayName}
        costCenterDisplayName={props.account.costCenter?.displayName}
        hidePrice={hidePrice && hidePriceFeature?.enabled}
        showQrCode={
          qrCodeIdentificationFeature?.enabled &&
          props.toggleAccountDetails !== undefined
        }
        localeKey={key}
        currencyFormatter={formatters.currency}
        toggleAccountDetails={props.toggleAccountDetails}
        showBalance={showBalance}
        toggleBalance={handleShowBalanceClick}
      />
    );
  },
);
